import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TLicenseSelectProps } from '@/console/features/licenses/components/license-select/license-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadLicensesForSelectQuery } from '@/shared/redux/api/assets/licenses/licenses-api';
import {
  LicenseForSelectFragment,
  ReadLicensesForSelectQuery,
} from '@/shared/types/types.generated';

export const LicenseSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  createOption,
  getItemId,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  required,
  licenseFilter,
}: TLicenseSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (license: LicenseForSelectFragment) => {
    return {
      label: license.product,
      value: license.licenseId,
      additionalInfo: license.status
        ? t(`licenses:license_status_enum.${license.status.toLowerCase()}`)
        : t('licenses:unknown'),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadLicensesForSelectQuery) => {
    if (!data) {
      return [];
    }

    const licenses = data?.readAllLicenses?.items;
    const validLicenses = licenseFilter
      ? licenses.filter((license) => licenseFilter(license))
      : licenses;

    const options = validLicenses.map((license) => _createOption(license));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadLicensesForSelectQuery,
      TOptionShape
    >
      required={required}
      name={name}
      label={t('common:license')}
      multiple
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadLicensesForSelectQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
    />
  );
};
